import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useDashboardLanguage } from './DashboardLanguageContext';
import { initBuzzer, playBuzzer } from '../sounds/buzzer';
import { validLicenses } from '../config/licenses';
import { MediaProvider } from './MediaContext';
import { inferiority, formatClock, createMatchStore } from '@rhs/core';
import { createBroadcastTransport } from '../sync/BroadcastTransport';

// Parse a "MM:SS" duration string to whole seconds.
const parseDurationToSeconds = (str) => {
  const [minutes, seconds] = String(str).split(':').map(Number);
  return (minutes || 0) * 60 + (seconds || 0);
};

const ScoreboardContext = createContext();

export const ScoreboardProvider = ({ children }) => {
  const { t } = useDashboardLanguage();
  // Load initial state from localStorage
  const loadInitialState = () => {
    try {
      const savedState = localStorage.getItem('scoreboard-state');
      if (savedState) {
        const parsed = JSON.parse(savedState);
        return {
          homeTeam: parsed.homeTeam,
          awayTeam: parsed.awayTeam,
          gameState: parsed.gameState,
          // STAGE 3: penalties + inferiority persist in the store's serializable shape
          // ({id,playerNumber,durationMs,startedAt} / {durationMs,startedAt}).
          homePenaltyState: parsed.homePenaltyState || [],
          awayPenaltyState: parsed.awayPenaltyState || [],
          homeInferiorityState: parsed.homeInferiorityState || [],
          awayInferiorityState: parsed.awayInferiorityState || [],
          backgroundColor: parsed.backgroundColor,
          defaultGameDuration: parsed.defaultGameDuration,
          defaultOvertime: parsed.defaultOvertime,
          carouselImages: parsed.carouselImages,
          breakOverlayImages: parsed.breakOverlayImages,
          // STAGE 4: timeout is owned by the MatchStore. Persist its serializable snapshot
          // (the running timeout + per-team hasTimeout/timeoutCount) and round-trip it below.
          timeoutSnapshot: parsed.timeoutSnapshot || null,
          showSettings: parsed.showSettings,
          defaultBreakDuration: parsed.defaultBreakDuration,
          overtimeBreak1Duration: parsed.overtimeBreak1Duration,
          overtimeBreak2Duration: parsed.overtimeBreak2Duration,
          autoBreakEnabled: parsed.autoBreakEnabled,
          // STAGE 5: the running break is owned by the MatchStore. Persist its serializable
          // snapshot ({durationMs, startedAt}|null) and round-trip it via store.applyBreak below.
          breakSnapshot: parsed.breakSnapshot || null,
          isBuzzerEnabled: parsed.isBuzzerEnabled,
          buzzerVolume: parsed.buzzerVolume,
          isEntertainmentEnabled: parsed.isEntertainmentEnabled,
          activeLicense: parsed.activeLicense,
          showTeamInferiority: parsed.showTeamInferiority ?? false,
        };
      }
    } catch (error) {
      console.warn('Error loading state from localStorage:', error);
    }
    return null;
  };

  const initialState = loadInitialState();

  // STAGE 4: timeout (running timeout + per-team hasTimeout/timeoutCount) is now owned by the
  // MatchStore. The stage-2 hasTimeout legacy shim (homeTeamLegacy/awayTeamLegacy) has been
  // DELETED — homeTeam/awayTeam read hasTimeout straight from the store below.

  // STAGE 3: penalties + inferiority are owned by the MatchStore (see below). The legacy
  // useState/refs/entry-system that mirrored them have been DELETED; these values are now
  // DERIVED from the store each render (see the derived projections after the store init).
  const [backgroundColor, setBackgroundColor] = useState(() => {
    return initialState?.backgroundColor || localStorage.getItem('backgroundColor') || '#1a374d';
  });
  const [defaultGameDuration, setDefaultGameDuration] = useState(() => {
    return initialState?.defaultGameDuration || localStorage.getItem('defaultGameDuration') || '25:00';
  });
  const [defaultOvertime, setDefaultOvertime] = useState(() => {
    return initialState?.defaultOvertime || localStorage.getItem('defaultOvertime') || '05:00';
  });
  const [carouselImages, setCarouselImages] = useState(() => {
    if (initialState?.carouselImages) return initialState.carouselImages;
    try {
      const saved = localStorage.getItem('carouselImages');
      return saved && saved !== 'undefined' ? JSON.parse(saved) : [];
    } catch (error) {
      console.warn('Error parsing carouselImages from localStorage:', error);
      return [];
    }
  });
  const [breakOverlayImages, setBreakOverlayImages] = useState(() => {
    if (initialState?.breakOverlayImages) return initialState.breakOverlayImages;
    try {
      const saved = localStorage.getItem('breakOverlayImages');
      return saved && saved !== 'undefined' ? JSON.parse(saved) : [];
    } catch (error) {
      console.warn('Error parsing breakOverlayImages from localStorage:', error);
      return [];
    }
  });
  const [showSettings, setShowSettings] = useState(initialState?.showSettings || false);
  const [showEntertainmentSettings, setShowEntertainmentSettings] = useState(false);
  // STAGE 4: the running timeout + per-team hasTimeout/timeoutCount live in the MatchStore.
  // Only two minimal pieces of UI-local state remain:
  //   • requestedTimeout — the "requested but NOT yet counting" team (the store's startTimeout
  //     begins the countdown, so the pre-start request phase is tracked here);
  //   • showTimeoutOverlay — whether the countdown overlay is visible (the running timeout in
  //     the store can exist while the overlay is shown; this distinguishes request vs running).
  const [requestedTimeout, setRequestedTimeout] = useState(null); // { team } | null
  const [showTimeoutOverlay, setShowTimeoutOverlay] = useState(false);
  const [defaultBreakDuration, setDefaultBreakDuration] = useState(initialState?.defaultBreakDuration || '10:00');
  const [overtimeBreak1Duration, setOvertimeBreak1Duration] = useState(initialState?.overtimeBreak1Duration || '03:00');
  const [overtimeBreak2Duration, setOvertimeBreak2Duration] = useState(initialState?.overtimeBreak2Duration || '02:00');
  const [autoBreakEnabled, setAutoBreakEnabled] = useState(() => {
    return initialState?.autoBreakEnabled ?? (localStorage.getItem('autoBreakEnabled') === 'true');
  });
  // STAGE 5: the RUNNING break (durationMs + startedAt) now lives in the MatchStore (see below).
  // showBreakOverlay/isBreakActive/breakTimeInSeconds are DERIVED from store.break each render.
  // Only the break-selection modal visibility stays UI-local (ephemeral; never persisted/synced).
  const [showBreakSelectionModal, setShowBreakSelectionModal] = useState(false);
  const [isBuzzerEnabled, setIsBuzzerEnabled] = useState(() => {
    return initialState?.isBuzzerEnabled ?? localStorage.getItem('isBuzzerEnabled') !== 'false';
  });
  const [buzzerVolume, setBuzzerVolume] = useState(() => {
    return initialState?.buzzerVolume ?? (parseFloat(localStorage.getItem('buzzerVolume')) || 0.5);
  });
  const [isEntertainmentEnabled, setIsEntertainmentEnabled] = useState(() => {
    return initialState?.isEntertainmentEnabled || localStorage.getItem('isEntertainmentEnabled') === 'true';
  });
  const [activeLicense, setActiveLicense] = useState(() => {
    return initialState?.activeLicense || localStorage.getItem('activeLicense') || '';
  });
  const [showTeamInferiority, setShowTeamInferiority] = useState(() => {
    return initialState?.showTeamInferiority ?? (localStorage.getItem('showTeamInferiority') === 'true');
  });



  const isMainWindow = useRef(window.opener === null);

  // ── MatchStore: single timestamp-based source of truth (002 store rework) ──
  // STAGE 1 migrated the clock; STAGE 3 migrates penalties + inferiority onto this SAME
  // store instance, so they share one time base and one start() instant — eliminating the
  // ~500ms penalty/clock desync. Teams/score/fouls, timeout and break remain legacy this stage.
  const matchStore = useRef(null);
  if (matchStore.current === null) {
    const persistedClock = initialState?.gameState;
    const initialClockSeconds = persistedClock
      ? persistedClock.timeInSeconds
      : parseDurationToSeconds(initialState?.defaultGameDuration || defaultGameDuration);
    // STAGE 6: the store now auto-syncs its WHOLE state across windows via a single
    // SyncTransport over BroadcastChannel('scoreboard-sync'). The MAIN window broadcasts on
    // every command; secondary windows apply incoming state without echoing back. This replaces
    // the entire hand-rolled per-field protocol (TICK/GAME_STATE/TEAM/PENALTIES/INFERIORITY/
    // TIMEOUT/BREAK/OVERTIME/RESET messages).
    const store = createMatchStore(undefined, {
      transport: createBroadcastTransport(),
      isMain: window.opener === null,
    });
    store.setPeriod(persistedClock?.period ?? 1);
    store.setClockRemaining(initialClockSeconds * 1000); // paused (startedAt: null)
    // STAGE 2: re-hydrate persisted team score/fouls/name into the store (now the source of truth).
    if (initialState?.homeTeam) {
      store.setName('home', initialState.homeTeam.name ?? '');
      store.setScore('home', initialState.homeTeam.score ?? 0);
      store.setFouls('home', initialState.homeTeam.fouls ?? 0);
    } else {
      store.setName('home', '');
    }
    if (initialState?.awayTeam) {
      store.setName('away', initialState.awayTeam.name ?? '');
      store.setScore('away', initialState.awayTeam.score ?? 0);
      store.setFouls('away', initialState.awayTeam.fouls ?? 0);
    } else {
      store.setName('away', '');
    }
    // Re-hydrate persisted penalties + inferiority (store serializable shapes).
    if (initialState?.homePenaltyState?.length) store.replacePenalties('home', initialState.homePenaltyState);
    if (initialState?.awayPenaltyState?.length) store.replacePenalties('away', initialState.awayPenaltyState);
    if (initialState?.homeInferiorityState?.length) store.replaceInferiority('home', initialState.homeInferiorityState);
    if (initialState?.awayInferiorityState?.length) store.replaceInferiority('away', initialState.awayInferiorityState);
    // STAGE 4: re-hydrate persisted timeout snapshot (running timeout + hasTimeout/timeoutCount).
    if (initialState?.timeoutSnapshot) store.applyTimeout(initialState.timeoutSnapshot);
    // STAGE 5: re-hydrate persisted running break ({durationMs, startedAt}|null). The absolute
    // startedAt restores the remaining correctly on reload.
    if (initialState?.breakSnapshot) store.applyBreak(initialState.breakSnapshot);
    matchStore.current = store;
  }
  const store = matchStore.current;

  const [centralTick, setCentralTick] = useState(0);

  // ── DERIVED team projections (STAGE 2 + STAGE 4) ──
  // score/fouls/name AND hasTimeout now all come from the MatchStore (the single source of
  // truth). Components keep reading homeTeam.name/score/fouls/hasTimeout unchanged.
  const homeTeam = {
    name: store.getState().home.name,
    score: store.getState().home.score,
    fouls: store.getState().home.fouls,
    hasTimeout: store.getState().home.hasTimeout,
  };
  const awayTeam = {
    name: store.getState().away.name,
    score: store.getState().away.score,
    fouls: store.getState().away.fouls,
    hasTimeout: store.getState().away.hasTimeout,
  };

  // ── DERIVED timeout state (STAGE 4) ──
  // activeTimeout is the legacy-shaped { team, timeLeft } exposed to consumers, derived from the
  // store. A running store timeout drives the live countdown (ceil(remaining/1000) for legacy
  // rounding); otherwise the pre-start "requested" team shows the fixed 60 s. Per-team counters
  // come straight from the store.
  const runningTimeout = store.getState().timeout;
  const activeTimeout = runningTimeout
    ? { team: runningTimeout.team, timeLeft: Math.ceil(store.timeoutRemainingMs(Date.now()) / 1000) }
    : requestedTimeout
      ? { team: requestedTimeout.team, timeLeft: 60 }
      : null;
  // STAGE 6: the timeout-overlay visibility is React-local UI state on the MAIN window
  // (showTimeoutOverlay distinguishes the request phase from the running countdown). The
  // secondary no longer receives a per-field TIMEOUT_UPDATE carrying that flag, so it DERIVES
  // overlay visibility purely from the mirrored running store timeout.
  const showTimeoutOverlayDerived = isMainWindow.current
    ? showTimeoutOverlay
    : runningTimeout !== null;
  const homeTimeoutCount = store.getState().home.timeoutCount;
  const awayTimeoutCount = store.getState().away.timeoutCount;
  // Stable boolean (avoids depending on the re-derived activeTimeout object identity in effects).
  const hasActiveTimeout = activeTimeout !== null;

  // ── DERIVED break state (STAGE 5) ──
  // The running break (durationMs + startedAt) is owned by the MatchStore. The overlay
  // visibility and the "active" flag are effectively the same in this UI (both were always
  // toggled together), so both are derived from store.break !== null. breakTimeInSeconds is the
  // live countdown: ceil(remaining/1000) preserves the legacy floor-elapsed rounding.
  const runningBreak = store.getState().break;
  const showBreakOverlay = runningBreak !== null;
  const isBreakActive = runningBreak !== null;
  const breakTimeInSeconds = runningBreak
    ? Math.ceil(store.breakRemainingMs(Date.now()) / 1000)
    : 0;

  // Team write commands (STAGE 2) — score/fouls/name go through the store; centralTick re-renders.
  const setTeamScore = (team, delta) => {
    store.setScore(team, delta);
    setCentralTick((prev) => prev + 1);
  };
  const setTeamName = (team, name) => {
    store.setName(team, name);
    setCentralTick((prev) => prev + 1);
  };
  // onFoulChange passes the new ABSOLUTE foul count; derive the delta from the store's current value.
  const setTeamFoulsAbsolute = (team, absolute) => {
    const current = store.getState()[team].fouls;
    store.setFouls(team, absolute - current);
    setCentralTick((prev) => prev + 1);
  };

  // ── Timeout command helpers (STAGE 4) — the new writers; the running timeout + counts live
  // in the store, the request/overlay UI phase lives in minimal local state above.
  const TIMEOUT_DURATION_MS = 60 * 1000;
  // Request: flag the team in the store (hasTimeout) without counting/starting the countdown.
  const requestTimeoutCmd = (team) => {
    if (store.getState().home.hasTimeout || store.getState().away.hasTimeout) return;
    store.requestTimeout(team);
    setRequestedTimeout({ team });
    setCentralTick((prev) => prev + 1);
  };
  // Start: begin the fixed 60 s countdown in the store (increments the per-team counter) and
  // open the overlay.
  const startTimeoutCmd = (team) => {
    store.startTimeout(team, TIMEOUT_DURATION_MS, Date.now());
    setShowTimeoutOverlay(true);
    setCentralTick((prev) => prev + 1);
  };
  // End / cancel / expiry: clear the running timeout + both teams' hasTimeout, close overlay.
  const endTimeoutCmd = () => {
    store.endTimeout();
    setRequestedTimeout(null);
    setShowTimeoutOverlay(false);
    setCentralTick((prev) => prev + 1);
  };
  // Manual per-team counter decrement (the red-dot buttons).
  const decreaseTimeoutCount = (team) => {
    const current = store.getState()[team].timeoutCount;
    if (current > 0) {
      store.applyTimeout({
        timeout: store.getState().timeout,
        home: { hasTimeout: store.getState().home.hasTimeout, timeoutCount: store.getState().home.timeoutCount - (team === 'home' ? 1 : 0) },
        away: { hasTimeout: store.getState().away.hasTimeout, timeoutCount: store.getState().away.timeoutCount - (team === 'away' ? 1 : 0) },
      });
      setCentralTick((prev) => prev + 1);
    }
  };

  // ── DERIVED penalty + inferiority projections (read-only; store is the sole writer) ──
  // Recomputed every render (centralTick drives re-render). Penalties are projected to the
  // legacy display shape { id, playerNumber, timeInSeconds } so the existing UI (which reads
  // .length / .id / .playerNumber / .timeInSeconds) keeps working unchanged. ceil(ms/1000)
  // preserves the legacy per-second rounding. Inferiority entries expose the store's queue
  // (consumers only check .length); inferiority seconds come from inferioritySeconds().
  const projectPenalties = (team) =>
    store.getState()[team].penalties.map((p) => ({
      id: p.id,
      playerNumber: p.playerNumber || '',
      timeInSeconds: Math.ceil(store.penaltyRemainingMs(team, p.id, Date.now()) / 1000),
    }));
  const homePenalties = projectPenalties('home');
  const awayPenalties = projectPenalties('away');
  const homeInferiorityEntries = store.getState().home.inferiority;
  const awayInferiorityEntries = store.getState().away.inferiority;
  const inferioritySeconds = (team) => Math.ceil(store.inferiorityMs(team, Date.now()) / 1000);

  // Penalty commands — the new writers (replace the legacy handlePenalty*/setPenalties paths).
  const PENALTY_DEFAULT_MS = 120 * 1000;
  const penaltyNextId = useRef(1);
  const addPenaltyCmd = (team) => {
    if (store.getState()[team].penalties.length >= 3) return;
    store.addPenalty(team, { id: `p${penaltyNextId.current++}`, durationMs: PENALTY_DEFAULT_MS, playerNumber: '' }, Date.now());
    setCentralTick((prev) => prev + 1);
  };
  const removePenaltyCmd = (team, id) => {
    store.removePenalty(team, id, Date.now());
    setCentralTick((prev) => prev + 1);
  };
  const editPenaltyTimeCmd = (team, id, newSeconds) => {
    store.editPenaltyTime(team, id, newSeconds * 1000, Date.now());
    setCentralTick((prev) => prev + 1);
  };
  const setPenaltyPlayerNumberCmd = (team, id, playerNumber) => {
    store.setPenaltyPlayerNumber(team, id, playerNumber);
    setCentralTick((prev) => prev + 1);
  };
  const clearPenaltiesCmd = (team) => {
    store.replacePenalties(team, []);
    store.replaceInferiority(team, []);
    setCentralTick((prev) => prev + 1);
  };

  // Derived clock seconds: ceil(ms/1000) reproduces the legacy `initialTime - floor(elapsed)`
  // rounding EXACTLY (a running clock's remaining has a sub-second part; ceil rounds it up).
  // STAGE 6: the secondary window now mirrors the WHOLE store state (incl. clock.startedAt) via
  // the transport, so it derives remaining locally from the absolute timestamps — no per-second
  // TICK message needed.
  const clockSeconds = () => Math.ceil(store.clockRemainingMs(Date.now()) / 1000);

  // gameState is now DERIVED from the store on BOTH windows (the running flag is the mirrored
  // store clock's startedAt). No separate secondary running flag.
  const isGameRunning = store.getState().clock.startedAt !== null;
  const gameState = {
    isRunning: isGameRunning,
    period: store.getState().clock.period,
    timeInSeconds: clockSeconds(),
  };

  // ── Clock command helpers (the new writers; replace reducer dispatches) ──
  const startStopClock = () => {
    if (store.getState().clock.startedAt !== null) {
      store.stop(Date.now());
    } else {
      store.start(Date.now());
    }
    setCentralTick((prev) => prev + 1);
  };
  const setClockTime = (seconds) => {
    store.setClockRemaining(seconds * 1000);
    setCentralTick((prev) => prev + 1);
  };
  const setClockPeriod = (n) => {
    store.setPeriod(n);
    setCentralTick((prev) => prev + 1);
  };
  // STAGE 6: store-backed reset (replaces the RESET_GAME dispatch shim — the LAST dispatch shim).
  // store.reset(durationMs) atomically clears scores/penalties/inferiority/timeout/break, sets
  // period 1, hasStarted=false and the clock to the default duration while KEEPING team names.
  // The single SyncTransport broadcasts this whole cleared state to the secondary window. The
  // resetAll window-event (below) then clears the remaining UI-local bits (names→'', request/
  // overlay flags) and is kept for any external listeners.
  const resetMatch = (durationStr = defaultGameDuration) => {
    const resetSeconds = parseDurationToSeconds(durationStr);
    store.reset(resetSeconds * 1000);
    window.dispatchEvent(new Event('resetAll'));
    setCentralTick((prev) => prev + 1);
  };

  const tickInterval = useRef(null);

  // Reset penalties + inferiority on resetAll event (store is the single source of truth).
  useEffect(() => {
    const handleReset = () => {
      store.replacePenalties('home', []);
      store.replacePenalties('away', []);
      store.replaceInferiority('home', []);
      store.replaceInferiority('away', []);
      // STAGE 2: reset team score/fouls/name (store is the source of truth) to match the
      // legacy reset that cleared name to '' and score/fouls to 0.
      for (const team of ['home', 'away']) {
        const t = store.getState()[team];
        store.setName(team, '');
        store.setScore(team, -t.score);
        store.setFouls(team, -t.fouls);
      }
      // STAGE 4: clear any running timeout + per-team hasTimeout/timeoutCount and the UI flags.
      store.endTimeout();
      store.resetTimeoutCounts();
      setRequestedTimeout(null);
      setShowTimeoutOverlay(false);
      // STAGE 5: clear any running break (store is the single source of truth).
      store.endBreak();
      setCentralTick((prev) => prev + 1);
    };
    window.addEventListener('resetAll', handleReset);
    return () => window.removeEventListener('resetAll', handleReset);
  }, []);

  // (The clock auto-stop + buzzer at 0 lives in the central tick below, sourced from the
  // MatchStore. See the central-tick useEffect.)

  // Handle period changes only when game is stopped
  useEffect(() => {
    if (isMainWindow.current && !gameState.isRunning && gameState.timeInSeconds === 0) {
      // Add a small delay to ensure all state updates are processed
      const timeoutId = setTimeout(() => {
        // Decide advancement via the shared @rhs/core MatchStore: regulation always advances,
        // overtime is entered only while the score is tied (FR-008/009). STAGE 2: the store now
        // holds the real score, so advancePeriod reads it directly (no seeding workaround).
        const periodDurationMs = parseDurationToSeconds(defaultGameDuration) * 1000;
        const overtimeDurationMs = parseDurationToSeconds(defaultOvertime) * 1000;
        const advance = store.advancePeriod({
          regularPeriods: 2,
          maxPeriods: 4,
          periodDurationMs,
          overtimeDurationMs,
        });

        if (advance.advanced) {
          const nextPeriod = advance.period;
          // STAGE 6: advancePeriod (and startBreak below) are store commands — the single
          // SyncTransport broadcasts the new period/clock/break to the secondary automatically.
          setCentralTick((prev) => prev + 1);

          // Start break overlay with appropriate duration based on next period (only if autoBreakEnabled)
          let breakDuration = null;
          if (autoBreakEnabled) {
            if (nextPeriod === 2) {
              breakDuration = defaultBreakDuration; // Between period 1 and 2
            } else if (nextPeriod === 3) {
              breakDuration = overtimeBreak1Duration; // Between period 2 and 3
            } else if (nextPeriod === 4) {
              breakDuration = overtimeBreak2Duration; // Between period 3 and 4
            }
          }

          if (breakDuration) {
            // STAGE 5: auto-start the matching break in the MatchStore (store auto-syncs).
            const [breakMinutes, breakSeconds] = breakDuration.split(':').map(Number);
            const breakTimeSeconds = breakMinutes * 60 + breakSeconds;
            store.startBreak(breakTimeSeconds * 1000, Date.now());
            setCentralTick((prev) => prev + 1);
          }
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [gameState.isRunning, gameState.timeInSeconds, gameState.period, homeTeam.score, awayTeam.score, defaultGameDuration, defaultOvertime, defaultBreakDuration, overtimeBreak1Duration, overtimeBreak2Duration, autoBreakEnabled]);

  // STAGE 6: the ENTIRE hand-rolled per-field BroadcastChannel protocol is GONE — no
  // TICK_UPDATE/GAME_STATE_UPDATE/TEAM_UPDATE/PENALTIES_UPDATE/INFERIORITY_ENTRIES_UPDATE/
  // TIMEOUT_UPDATE/BREAK_UPDATE/OVERTIME_START/RESET_GAME messages, no receive handler, no
  // per-field broadcast-send effects. The MatchStore's single SyncTransport mirrors the WHOLE
  // state across windows automatically (main broadcasts on every command; secondary applies
  // without echo). Here we only re-render React when the store changes (the central tick already
  // re-renders every 100ms for the derived countdowns, but a subscription gives instant updates
  // for discrete changes like score, and — on the secondary — for incoming patches).
  useEffect(() => {
    const unsubscribe = store.subscribe(() => setCentralTick((prev) => prev + 1));
    return unsubscribe;
  }, []);

  // Format time in MM:SS format
  // Shared formatter from @rhs/core (mm:ss). Identical output for integer seconds.
  const formatTime = (seconds) => formatClock(seconds * 1000);

  // Calculate elapsed time for the entire game
  const calculateElapsedTime = () => {
    const currentTime = calculateCurrentTime();
    const [defaultMinutes, defaultSeconds] = defaultGameDuration.split(':').map(Number);
    const [overtimeMinutes, overtimeSeconds] = defaultOvertime.split(':').map(Number);
    const defaultTimeInSeconds = defaultMinutes * 60 + defaultSeconds;
    const overtimeInSeconds = overtimeMinutes * 60 + overtimeSeconds;
    
    if (gameState.period <= 2) {
      // For periods 1 and 2: period * defaultGameTime + elapsed time
      return (gameState.period - 1) * defaultTimeInSeconds + (defaultTimeInSeconds - currentTime);
    } else {
      // For periods 3 and 4: 2 * defaultGameTime + (period-2) * defaultOvertime + elapsed time
      const overtimePeriod = gameState.period - 2;
      return (2 * defaultTimeInSeconds) + 
             ((overtimePeriod-1) * overtimeInSeconds) + 
             (overtimeInSeconds - currentTime);
    }
  };

  // Calculate current game time based on real time without state updates.
  // Sourced from the MatchStore; ceil(ms/1000) reproduces the legacy
  // `initialTime - floor(elapsed)` rounding exactly.
  const calculateCurrentTime = () => clockSeconds();

  // Central tick mechanism with time updates
  useEffect(() => {
    const updateTimes = () => {
      setCentralTick(prev => prev + 1);

      // STAGE 6: the 100ms central tick runs on BOTH windows so the secondary re-renders its
      // derived countdown from the mirrored absolute timestamps. ONLY the MAIN window performs
      // side-effects (auto-stop at 0, buzzer, advancePeriod, clearExpiredPenalties,
      // advanceInferiority, timeout/break expiry); the secondary purely mirrors + derives.
      //
      // Clock auto-stop at 0 (main only): the MatchStore clock runs on its own absolute
      // timestamp; the store auto-broadcasts the stop to the secondary via the transport.
      if (isMainWindow.current && store.getState().clock.startedAt !== null) {
        if (clockSeconds() === 0) {
          // Period ended: stop the clock (buzzer plays via the timeInSeconds===0 effect),
          // then the auto-period-advance effect handles progression.
          store.stop(Date.now());
        }
      }

      // Penalties + inferiority are MatchStore-owned (STAGE 3). On the main window, while the
      // clock runs, clear expired penalties (independent, parallel) and advance the inferiority
      // queue (sequential drain). The store mutates and auto-broadcasts the change.
      if (isMainWindow.current && store.getState().clock.startedAt !== null) {
        const now = Date.now();
        store.clearExpiredPenalties(now);
        store.advanceInferiority(now);
      }

      // STAGE 4: timeout countdown (main window). The running timeout lives in the store; here we
      // just detect expiry. centralTick re-render above re-derives activeTimeout's ceil seconds.
      if (isMainWindow.current && showTimeoutOverlay && store.getState().timeout) {
        const remaining = Math.ceil(store.timeoutRemainingMs(Date.now()) / 1000);
        if (remaining === 0) {
          // Mirror the End button logic on expiry: buzzer, clear store timeout + hasTimeout,
          // close overlay.
          playBuzzer();
          store.endTimeout();
          setRequestedTimeout(null);
          setShowTimeoutOverlay(false);
        }
      }

      // STAGE 5: break countdown (main window). The running break lives in the store; here we
      // detect expiry — fire the buzzer EXACTLY ONCE as the displayed seconds reach 0, then clear
      // store.break (closing the overlay). The store auto-broadcasts the cleared break to the
      // secondary; centralTick re-render above re-derives the ceil seconds.
      if (isMainWindow.current && store.getState().break) {
        const remaining = Math.ceil(store.breakRemainingMs(Date.now()) / 1000);
        if (remaining === 0) {
          playBuzzer();
          store.endBreak();
        }
      }
    };

    // Use a more reasonable interval for UI updates (100ms)
    tickInterval.current = setInterval(updateTimes, 100);
    return () => clearInterval(tickInterval.current);
  }, [gameState.isRunning, gameState.timeInSeconds, gameState.period, defaultOvertime, defaultGameDuration, showTimeoutOverlay]);

  // (Penalty + inferiority start/stop timing is owned by the MatchStore: store.start(now)/
  // store.stop(now) — invoked from the clock start/stop helpers — start and pause penalties and
  // the inferiority front segment at the SAME instant as the clock. No separate effect needed.)

  // STAGE 5: break write helpers — the running break lives in the MatchStore (store.startBreak/
  // store.endBreak). The store's SyncTransport auto-mirrors the break to the secondary windows.

  // Start a manual break with selected duration (via the selection modal).
  const startManualBreak = useCallback((breakType) => {
    let breakDuration = null;

    if (breakType === 'p1-p2') {
      breakDuration = defaultBreakDuration;
    } else if (breakType === 'p2-p3') {
      breakDuration = overtimeBreak1Duration;
    } else if (breakType === 'p3-p4') {
      breakDuration = overtimeBreak2Duration;
    }

    if (breakDuration) {
      const [breakMinutes, breakSeconds] = breakDuration.split(':').map(Number);
      const breakTimeSeconds = breakMinutes * 60 + breakSeconds;
      store.startBreak(breakTimeSeconds * 1000, Date.now());
      setShowBreakSelectionModal(false);
      setCentralTick((prev) => prev + 1);
    }
  }, [defaultBreakDuration, overtimeBreak1Duration, overtimeBreak2Duration]);

  // End the running break (manual "End Break" button or natural expiry) → clears store.break.
  const endBreakCmd = useCallback(() => {
    store.endBreak();
    setCentralTick((prev) => prev + 1);
  }, []);

  // ============================================
  // INFERIORITY — MatchStore-derived shim (legacy entry system DELETED)
  // ============================================
  // calculateInferiorityTime keeps the legacy call signature used by ScoreboardLive /
  // TeamInferiority: it is passed the store's inferiority QUEUE (segments {durationMs,
  // startedAt}) and returns whole seconds. ceil(ms/1000) preserves the legacy rounding.
  const calculateInferiorityTime = (queue) => {
    if (!queue || queue.length === 0) return 0;
    return Math.ceil(inferiority.totalInferiorityMs(queue, Date.now()) / 1000);
  };


  // STAGE 4: end the timeout via the store (clears running timeout + both teams' hasTimeout).
  const handleTimeoutEnd = () => {
    store.endTimeout();
    setRequestedTimeout(null);
    setShowTimeoutOverlay(false);
    setCentralTick((prev) => prev + 1);
  };

  // Initialize buzzer sound
  useEffect(() => {
    initBuzzer();
  }, []);

  // Update localStorage when buzzer settings change
  useEffect(() => {
    localStorage.setItem('isBuzzerEnabled', isBuzzerEnabled);
  }, [isBuzzerEnabled]);

  useEffect(() => {
    localStorage.setItem('buzzerVolume', buzzerVolume.toString());
  }, [buzzerVolume]);

  useEffect(() => {
    localStorage.setItem('autoBreakEnabled', autoBreakEnabled.toString());
  }, [autoBreakEnabled]);

  useEffect(() => {
    localStorage.setItem('breakOverlayImages', JSON.stringify(breakOverlayImages));
  }, [breakOverlayImages]);

  // Track previous states
  const prevGameRunningRef = useRef(false);
  const prevActiveTimeoutRef = useRef(false);
  const prevPeriodRef = useRef(1);

  // Play buzzer when timer reaches zero, game is paused, or timeout is requested
  useEffect(() => {
    // Case 1: Timer reaches zero
    if (gameState.timeInSeconds === 0) {
      playBuzzer();
      return;
    }

    // Case 2: Game is paused while timeout is active, or timeout requested while game is paused.
    // STAGE 4: hasActiveTimeout is the store-derived boolean (requested OR running).
    if ((prevGameRunningRef.current && !gameState.isRunning && hasActiveTimeout) ||
        (!gameState.isRunning && !prevActiveTimeoutRef.current && hasActiveTimeout)) {
      playBuzzer();
    }

    // Update previous states
    prevGameRunningRef.current = gameState.isRunning;
    prevActiveTimeoutRef.current = hasActiveTimeout;
  }, [gameState.timeInSeconds, gameState.isRunning, hasActiveTimeout]);

  // STAGE 4: the per-team timeout counter is incremented by store.startTimeout (counted ONCE
  // per start), so the legacy rising-edge counter effect has been DELETED.

  // Effect to reset timeout counters when period changes (E3 / Timeout 5) — via the store.
  useEffect(() => {
    if (gameState.period !== prevPeriodRef.current) {
      store.resetTimeoutCounts();
      setCentralTick((prev) => prev + 1);
      prevPeriodRef.current = gameState.period;
    }
  }, [gameState.period]);

  const validateLicense = (licenseInput) => {
    const [name, key] = licenseInput.split(':');
    if (!name || !key) return false;
    
    return validLicenses[name] === key;
  };

  const activateLicense = (licenseInput) => {
    if (validateLicense(licenseInput)) {
      setActiveLicense(licenseInput);
      setIsEntertainmentEnabled(true);
      localStorage.setItem('activeLicense', licenseInput);
      localStorage.setItem('isEntertainmentEnabled', 'true');
      return true;
    }
    return false;
  };

  const deactivateLicense = () => {
    setActiveLicense('');
    setIsEntertainmentEnabled(false);
    localStorage.removeItem('activeLicense');
    localStorage.setItem('isEntertainmentEnabled', 'false');
  };

  // Save state to localStorage whenever it changes
  useEffect(() => {
    const state = {
      homeTeam,
      awayTeam,
      gameState,
      // STAGE 3: persist penalties + inferiority in the store's serializable shape so reload
      // round-trips them (timestamps are absolute and restore remaining correctly).
      homePenaltyState: store.getState().home.penalties,
      awayPenaltyState: store.getState().away.penalties,
      homeInferiorityState: store.getState().home.inferiority,
      awayInferiorityState: store.getState().away.inferiority,
      backgroundColor,
      defaultGameDuration,
      defaultOvertime,
      carouselImages,
      breakOverlayImages,
      // STAGE 4: persist the store's timeout snapshot (running timeout + per-team
      // hasTimeout/timeoutCount) so reload round-trips it via store.applyTimeout.
      timeoutSnapshot: {
        timeout: store.getState().timeout,
        home: { hasTimeout: store.getState().home.hasTimeout, timeoutCount: store.getState().home.timeoutCount },
        away: { hasTimeout: store.getState().away.hasTimeout, timeoutCount: store.getState().away.timeoutCount },
      },
      showSettings,
      showEntertainmentSettings,
      defaultBreakDuration,
      overtimeBreak1Duration,
      overtimeBreak2Duration,
      autoBreakEnabled,
      // STAGE 5: persist the store's running-break snapshot ({durationMs, startedAt}|null) so
      // reload round-trips it via store.applyBreak.
      breakSnapshot: store.getState().break,
      isBuzzerEnabled,
      buzzerVolume,
      isEntertainmentEnabled,
      activeLicense,
      showTeamInferiority
    };
    localStorage.setItem('scoreboard-state', JSON.stringify(state));
  }, [
    homeTeam.name,
    homeTeam.score,
    homeTeam.fouls,
    homeTeam.hasTimeout,
    awayTeam.name,
    awayTeam.score,
    awayTeam.fouls,
    awayTeam.hasTimeout,
    gameState.isRunning,
    gameState.period,
    gameState.timeInSeconds,
    centralTick,
    backgroundColor,
    defaultGameDuration,
    defaultOvertime,
    carouselImages,
    breakOverlayImages,
    showTimeoutOverlay,
    showSettings,
    showEntertainmentSettings,
    defaultBreakDuration,
    overtimeBreak1Duration,
    overtimeBreak2Duration,
    autoBreakEnabled,
    isBuzzerEnabled,
    buzzerVolume,
    isEntertainmentEnabled,
    activeLicense,
    homeTimeoutCount,
    awayTimeoutCount,
    showTeamInferiority
  ]);

  const value = {
    homeTeam,
    awayTeam,
    // STAGE 2: store-backed team write commands (score/fouls/name).
    setTeamScore,
    setTeamName,
    setTeamFoulsAbsolute,
    backgroundColor,
    setBackgroundColor,
    defaultGameDuration,
    setDefaultGameDuration,
    defaultOvertime,
    setDefaultOvertime,
    carouselImages,
    setCarouselImages,
    breakOverlayImages,
    setBreakOverlayImages,
    showSettings,
    setShowSettings,
    showEntertainmentSettings,
    setShowEntertainmentSettings,
    // STAGE 4: timeout state (derived from the store) + command helpers (the new writers).
    activeTimeout,
    // STAGE 6: secondary derives overlay visibility from the mirrored running store timeout.
    showTimeoutOverlay: showTimeoutOverlayDerived,
    setShowTimeoutOverlay,
    requestTimeout: requestTimeoutCmd,
    startTimeout: startTimeoutCmd,
    endTimeout: endTimeoutCmd,
    decreaseTimeoutCount,
    defaultBreakDuration,
    setDefaultBreakDuration,
    overtimeBreak1Duration,
    setOvertimeBreak1Duration,
    overtimeBreak2Duration,
    setOvertimeBreak2Duration,
    autoBreakEnabled,
    setAutoBreakEnabled,
    // STAGE 5: break overlay/active/seconds are DERIVED from the store's running break (read-only).
    // Mutate via startManualBreak (selection modal / auto) and endBreak (manual stop / expiry).
    showBreakOverlay,
    isBreakActive,
    breakTimeInSeconds,
    showBreakSelectionModal,
    setShowBreakSelectionModal,
    startManualBreak,
    endBreak: endBreakCmd,
    // Penalties — derived (read-only) projections of the store; mutate via the commands below.
    homePenalties,
    awayPenalties,
    addPenalty: addPenaltyCmd,
    removePenalty: removePenaltyCmd,
    editPenaltyTime: editPenaltyTimeCmd,
    setPenaltyPlayerNumber: setPenaltyPlayerNumberCmd,
    clearPenalties: clearPenaltiesCmd,
    timeInSeconds: gameState.timeInSeconds,
    period: gameState.period,
    isGameRunning: gameState.isRunning,
    // STAGE 6: store-backed reset (replaces the RESET_GAME dispatch shim — last dispatch gone).
    resetMatch,
    // Clock commands (MatchStore-backed).
    startStopClock,
    setClockTime,
    setClockPeriod,
    centralTick,
    calculateCurrentTime,
    calculateElapsedTime,
    formatTime,
    gameState,
    handleTimeoutEnd,
    isBuzzerEnabled,
    setIsBuzzerEnabled,
    buzzerVolume,
    setBuzzerVolume,
    isEntertainmentEnabled,
    activeLicense,
    activateLicense,
    deactivateLicense,
    homeTimeoutCount,
    awayTimeoutCount,
    showTeamInferiority,
    setShowTeamInferiority,
    // Inferiority — store-derived (the legacy entry system is DELETED). The *Entries values
    // are the store's inferiority QUEUE (consumers only check .length); calculateInferiorityTime
    // turns a queue into whole seconds.
    homeInferiorityEntries,
    awayInferiorityEntries,
    calculateInferiorityTime,
  };

  return (
    <ScoreboardContext.Provider value={value}>
      <MediaProvider isGameRunning={gameState.isRunning}>
        {children}
      </MediaProvider>
    </ScoreboardContext.Provider>
  );
};

export const useScoreboard = () => {
  const context = useContext(ScoreboardContext);
  if (!context) {
    throw new Error('useScoreboard must be used within a ScoreboardProvider');
  }
  return context;
};
